// TL tiered discount.
class TLTieredDiscount extends HTMLElement {
  #productDetails = null;
  #abortCtrl = null;
  #warmupCtrl = null;
  #observer = null;
  #initialised = false;
  #sectionCtrl = null;

  connectedCallback() {
    if (this.#sectionCtrl) return;

    this.#sectionCtrl = new AbortController();
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, {
      signal: this.#sectionCtrl.signal
    });

    if (this.#initialised) return;

    this.#warmupCtrl = new AbortController();
    const warmupOpts = { signal: this.#warmupCtrl.signal, once: true };
    this.addEventListener('focusin', this.#init, warmupOpts);
    this.addEventListener('pointerdown', this.#init, warmupOpts);

    if (!('IntersectionObserver' in window)) {
      this.#init();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#init();
      },
      { rootMargin: '1000px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#warmupCtrl?.abort();
    this.#warmupCtrl = null;
    this.#abortCtrl?.abort();
    this.#abortCtrl = null;
    this.#sectionCtrl?.abort();
    this.#sectionCtrl = null;
    this.#productDetails = null;
    this.#initialised = false;
  }

  #handleSectionLoad = (event) => {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  };

  #init = () => {
    if (this.#initialised) return;
    this.#initialised = true;
    this.#warmupCtrl?.abort();
    this.#warmupCtrl = null;
    this.#observer?.disconnect();
    this.#observer = null;

    this.#productDetails = this.closest('.product-details');
    this.#abortCtrl = new AbortController();
    const opts = { signal: this.#abortCtrl.signal };
    this.addEventListener('change', this.#handleChange, opts);
    this.#productDetails?.addEventListener('quantity-selector:update', this.#handleQuantityUpdate, opts);
    this.#syncFromQuantitySelector();
  };

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const quantity = Number(input.value);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    const quantitySelector = this.#getQuantitySelector();
    const quantityInput = quantitySelector?.querySelector('input[name="quantity"]');

    if (!(quantityInput instanceof HTMLInputElement)) return;

    if (typeof quantitySelector.setValue === 'function') {
      quantitySelector.setValue(String(quantity));
    } else {
      quantityInput.value = String(quantity);
    }

    if (typeof quantitySelector.onQuantityChange === 'function') {
      quantitySelector.onQuantityChange();
    } else {
      quantitySelector.dispatchEvent(
        new CustomEvent('quantity-selector:update', {
          bubbles: true,
          detail: { quantity },
        })
      );
    }

    if (typeof quantitySelector.updateButtonStates === 'function') quantitySelector.updateButtonStates();
  };

  #handleQuantityUpdate = (event) => {
    const quantitySelector = event.target;
    if (!(quantitySelector instanceof HTMLElement) || !quantitySelector.matches('quantity-selector-component')) return;
    if (quantitySelector !== this.#getQuantitySelector()) return;

    const quantity = Number(event.detail?.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    this.#syncSelection(quantity);
  };

  #getQuantitySelector() {
    return this.#productDetails?.querySelector('quantity-selector-component') ?? null;
  }

  #syncFromQuantitySelector() {
    const quantityInput = this.#getQuantitySelector()?.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const quantity = Number(quantityInput.value);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    this.#syncSelection(quantity);
  }

  #syncSelection(quantity) {
    const inputs = Array.from(this.querySelectorAll('.tl-tiered-discount__input'));
    const matchingInput = inputs.reduce((bestMatch, input) => {
      const tierQuantity = Number(input.value);
      if (!Number.isFinite(tierQuantity) || tierQuantity > quantity) return bestMatch;

      const bestQuantity = bestMatch ? Number(bestMatch.value) : 0;
      return tierQuantity >= bestQuantity ? input : bestMatch;
    }, null);

    for (const input of inputs) {
      input.checked = input === matchingInput;
    }
  }
}

if (!customElements.get('tl-tiered-discount')) {
  customElements.define('tl-tiered-discount', TLTieredDiscount);
}
